import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ds';
import {
  AUTH_BUTTON_STYLE,
  AuthError,
  AuthHeading,
  AuthLayout,
  TextField,
} from '@/components/auth/AuthLayout';
import { MIN_PASSWORD_LENGTH, resetPassword } from '@/lib/session';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const invalidToken = searchParams.get('error') === 'INVALID_TOKEN' || !token;
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [showFieldErrors, setShowFieldErrors] = useState(false);

  const passwordError =
    password.length > 0 && password.length < MIN_PASSWORD_LENGTH
      ? `Use at least ${MIN_PASSWORD_LENGTH} characters.`
      : '';
  const confirmError = confirm.length > 0 && confirm !== password ? 'Passwords do not match.' : '';
  const canSubmit =
    Boolean(token) && password.length >= MIN_PASSWORD_LENGTH && confirm === password && !busy;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setShowFieldErrors(true);
    if (!canSubmit || !token) return;

    setBusy(true);
    setError('');
    try {
      await resetPassword(token, password);
      setComplete(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset your password.');
      setBusy(false);
    }
  };

  if (invalidToken) {
    return (
      <AuthLayout>
        <div className="flex flex-col gap-5">
          <AuthHeading title="This reset link is not valid">
            It may have expired or already been used. Request a new link to continue.
          </AuthHeading>
          <Link
            to="/forgot-password"
            className="text-sm font-semibold text-forest underline-offset-2 hover:underline"
          >
            Request a new link
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (complete) {
    return (
      <AuthLayout>
        <div className="flex flex-col gap-5">
          <AuthHeading title="Password updated">
            You can now sign in with your new password.
          </AuthHeading>
          <Link
            to="/login"
            className="text-sm font-semibold text-forest underline-offset-2 hover:underline"
          >
            Continue to sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form className="flex flex-col gap-5" onSubmit={submit} noValidate>
        <AuthHeading title="Choose a new password">
          Use at least {MIN_PASSWORD_LENGTH} characters.
        </AuthHeading>

        <AuthError>{error}</AuthError>

        <TextField
          label="New password"
          type="password"
          name="password"
          autoComplete="new-password"
          autoFocus
          required
          placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          disabled={busy}
          error={showFieldErrors ? passwordError : ''}
        />

        <TextField
          label="Confirm new password"
          type="password"
          name="confirmPassword"
          autoComplete="new-password"
          required
          placeholder="Type it again"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          disabled={busy}
          error={showFieldErrors || confirm.length > 0 ? confirmError : ''}
        />

        <Button type="submit" style={AUTH_BUTTON_STYLE} disabled={busy}>
          {busy ? 'Updating password…' : 'Set new password'}
        </Button>
      </form>
    </AuthLayout>
  );
}

export default ResetPasswordPage;
